import {
  ChoiceDefinition,
  InstrumentDefinition,
  ItemDefinition,
  QuestionnaireFormDefinition,
  QuestionnaireResponseType,
} from '@app/api/QuestionnaireAdminStore';

/** An existing item whose content the admin changed, so publishing creates a new version of it. */
export interface ChangedItem {
  code: string;
  before: ItemDefinition;
  after: ItemDefinition;
}

/** For each changed item code: true when the change alters what the question means. */
export type MeaningAnswers = Record<string, boolean>;

export const emptyChoice = (): ChoiceDefinition => ({ text: '', scoreValue: null });

export const emptyItem = (): ItemDefinition => ({
  code: '',
  text: '',
  leadIn: null,
  responseType: QuestionnaireResponseType.MutuallyExclusive,
  subscaleCode: null,
  choices: [emptyChoice()],
});

export const emptyInstrument = (): InstrumentDefinition => ({ code: '', name: '', items: [emptyItem()] });

export const emptyDefinition = (): QuestionnaireFormDefinition => ({
  code: '',
  name: '',
  instruments: [emptyInstrument()],
});

/** Moves the entry at index by offset (-1 up, 1 down). Moving past either end leaves the list as it was. */
export const moveInList = <T>(list: T[], index: number, offset: number): T[] => {
  const target = index + offset;
  if (target < 0 || target >= list.length) return list;
  const moved = [...list];
  [moved[index], moved[target]] = [moved[target], moved[index]];
  return moved;
};

export const replaceAt = <T>(list: T[], index: number, replace: (entry: T) => T): T[] =>
  list.map((entry, position) => (position === index ? replace(entry) : entry));

export const removeAt = <T>(list: T[], index: number): T[] => list.filter((_, position) => position !== index);

/** Puts a copy of the entry at index straight after it. */
export const duplicateAt = <T>(list: T[], index: number, copy: (entry: T) => T): T[] => [
  ...list.slice(0, index + 1),
  copy(list[index]),
  ...list.slice(index + 1),
];

// A copy gets a blank code, since codes are identities: keeping the original's would make publishing reject it as
// appearing twice. It's also a new item, so it has no earlier version to be comparable to.
export const copyItem = (item: ItemDefinition): ItemDefinition => {
  const copy: ItemDefinition = { ...item, code: '', choices: item.choices.map((choice) => ({ ...choice })) };
  delete copy.comparableToPreviousVersion;
  return copy;
};

/** A copy of the instrument and its items, every code blank. */
export const copyInstrument = (instrument: InstrumentDefinition): InstrumentDefinition => ({
  code: '',
  name: `${instrument.name} (copy)`,
  items: instrument.items.map(copyItem),
});

export const updateInstruments = (
  definition: QuestionnaireFormDefinition,
  change: (instruments: InstrumentDefinition[]) => InstrumentDefinition[]
): QuestionnaireFormDefinition => ({ ...definition, instruments: change(definition.instruments) });

export const updateInstrument = (
  definition: QuestionnaireFormDefinition,
  instrumentIndex: number,
  change: (instrument: InstrumentDefinition) => InstrumentDefinition
): QuestionnaireFormDefinition =>
  updateInstruments(definition, (instruments) => replaceAt(instruments, instrumentIndex, change));

export const updateItem = (
  definition: QuestionnaireFormDefinition,
  instrumentIndex: number,
  itemIndex: number,
  change: (item: ItemDefinition) => ItemDefinition
): QuestionnaireFormDefinition =>
  updateInstrument(definition, instrumentIndex, (instrument) => ({
    ...instrument,
    items: replaceAt(instrument.items, itemIndex, change),
  }));

export const updateChoice = (
  definition: QuestionnaireFormDefinition,
  instrumentIndex: number,
  itemIndex: number,
  choiceIndex: number,
  change: (choice: ChoiceDefinition) => ChoiceDefinition
): QuestionnaireFormDefinition =>
  updateItem(definition, instrumentIndex, itemIndex, (item) => ({
    ...item,
    choices: replaceAt(item.choices, choiceIndex, change),
  }));

/**
 * The definition as the publish endpoint should get it: text trimmed, empty lead-ins and subscales sent as null,
 * and free-text items sent without choices.
 */
export const cleanForPublish = (definition: QuestionnaireFormDefinition): QuestionnaireFormDefinition => ({
  code: definition.code.trim(),
  name: definition.name.trim(),
  ...(definition.changeReason !== undefined && { changeReason: definition.changeReason.trim() }),
  instruments: definition.instruments.map((instrument) => ({
    code: instrument.code.trim(),
    name: instrument.name.trim(),
    items: instrument.items.map((item) => {
      const cleaned: ItemDefinition = {
        code: item.code.trim(),
        text: item.text.trim(),
        leadIn: item.leadIn?.trim() || null,
        responseType: item.responseType,
        subscaleCode: item.subscaleCode?.trim() || null,
        choices:
          item.responseType === QuestionnaireResponseType.FreeText
            ? []
            : item.choices.map((choice) => ({
                text: choice.text.trim(),
                scoreValue: Number.isFinite(choice.scoreValue) ? choice.scoreValue : null,
              })),
      };
      if (item.comparableToPreviousVersion !== undefined) {
        cleaned.comparableToPreviousVersion = item.comparableToPreviousVersion;
      }
      return cleaned;
    }),
  })),
});

/** True when the edits would publish exactly what was loaded. */
export const sameDefinition = (left: QuestionnaireFormDefinition, right: QuestionnaireFormDefinition): boolean =>
  JSON.stringify(cleanForPublish(left)) === JSON.stringify(cleanForPublish(right));

/** Codes already published, which the editor locks: changing one would start a new instrument or item. */
export const publishedCodes = (published: QuestionnaireFormDefinition | null) => ({
  instrumentCodes: new Set(published?.instruments.map((instrument) => instrument.code) ?? []),
  itemCodes: new Set(published?.instruments.flatMap((instrument) => instrument.items.map((item) => item.code)) ?? []),
});

const allItems = (definition: QuestionnaireFormDefinition): ItemDefinition[] =>
  definition.instruments.flatMap((instrument) => instrument.items);

// What publishing compares to decide an item needs a new version. The subscale belongs to the instrument
// placement, not the item, so it isn't part of this.
const itemContent = (item: ItemDefinition) => ({
  text: item.text,
  leadIn: item.leadIn ?? null,
  responseType: item.responseType,
  choices: item.choices,
});

/**
 * Items that were published before and whose wording, lead-in, response type or choices changed. Publishing gives
 * each a new version, so the admin is asked whether each change alters what the question means. New items aren't
 * included: they have no earlier version to compare with.
 */
export const changedPublishedItems = (
  published: QuestionnaireFormDefinition | null,
  edited: QuestionnaireFormDefinition
): ChangedItem[] => {
  if (!published) return [];
  const publishedItems = allItems(cleanForPublish(published));
  return allItems(cleanForPublish(edited)).flatMap((after) => {
    const before = publishedItems.find((item) => item.code === after.code);
    if (!before || JSON.stringify(itemContent(before)) === JSON.stringify(itemContent(after))) return [];
    return [{ code: after.code, before, after }];
  });
};

/** Records each answer on its item as comparableToPreviousVersion, the opposite of "changes the meaning". */
export const withMeaningAnswers = (
  definition: QuestionnaireFormDefinition,
  answers: MeaningAnswers
): QuestionnaireFormDefinition => ({
  ...definition,
  instruments: definition.instruments.map((instrument) => ({
    ...instrument,
    items: instrument.items.map((item) => {
      const answered = { ...item };
      delete answered.comparableToPreviousVersion;
      if (item.code in answers) answered.comparableToPreviousVersion = !answers[item.code];
      return answered;
    }),
  })),
});
